import { useCallback, useRef, useState } from 'react';
import { AppShell, type ShellTab } from './components/AppShell';
import { PageHeader } from './components/PageHeader';
import { TextAreaField } from './components/ui/Field';
import { Button } from './components/ui';
import { ResultsPanel } from './components/ResultsPanel';
import { AnalyzeIcon, ProfileIcon, RefreshIcon, ResumeIcon } from './components/ui/icons';
import {
  ResumeUpload,
  type UploadDocInfo,
  type UploadPhase,
} from './components/ResumeUpload';
import { analyzeMatch } from './lib/analyze';
import { extractTextFromFile } from './lib/extract';
import { validateResumeFile } from './lib/files';
import { hasErrors, validateInputs } from './lib/validation';
import type { FieldErrors } from './lib/validation';
import type { AnalysisResult } from './lib/types';
import { ProfilePanel } from './components/profile/ProfilePanel';
import { ImportReviewPanel } from './components/profile/ImportReviewPanel';
import { emptyProfile } from './lib/profile/model';
import type { ImportSession, MasterProfile } from './lib/profile/types';
import { createImportSession } from './lib/profile/merge';
import { ResumeBuilder } from './components/resume/ResumeBuilder';
import { draftFromProfile } from './lib/resume/build';
import { restoreVersion, saveVersion } from './lib/resume/versions';
import type { ResumeDraft, ResumeVersion } from './lib/resume/types';

type Status = 'idle' | 'loading' | 'success' | 'error';

const RESUME_SAMPLE =
  'Senior React engineer. Built accessible dashboards with TypeScript, Vite, and Tailwind CSS. Led design systems and end-to-end testing with Playwright.';
const JOB_SAMPLE =
  'Looking for a frontend engineer expert in React, TypeScript, Vite, accessibility, and automated testing with Playwright. Tailwind CSS a plus.';

export default function App() {
  const [resume, setResume] = useState('');
  const [job, setJob] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [message, setMessage] = useState('');
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const [uploadMessage, setUploadMessage] = useState('');
  const [docInfo, setDocInfo] = useState<UploadDocInfo | null>(null);
  const [activeTab, setActiveTab] = useState<ShellTab>('analyze');
  const [profile, setProfile] = useState<MasterProfile>(emptyProfile());
  const [importSession, setImportSession] = useState<ImportSession | null>(null);
  const [resumeDraft, setResumeDraft] = useState<ResumeDraft | null>(null);
  const [resumeVersions, setResumeVersions] = useState<ResumeVersion[]>([]);
  const resultsRef = useRef<HTMLDivElement>(null);
  const lastExtractedRef = useRef<string | null>(null);

  const handleFileSelected = useCallback(async (file: File) => {
    setUploadPhase('extracting');
    setUploadMessage(`Reading "${file.name}" locally…`);
    try {
      const validation = await validateResumeFile(file);
      if (!validation.ok) {
        setUploadPhase('error');
        setUploadMessage(validation.error);
        return;
      }

      const extracted = await extractTextFromFile(file, validation.kind);
      if (!extracted.ok) {
        setUploadPhase('error');
        setUploadMessage(extracted.error);
        return;
      }

      // Snapshot the previous extraction before updating the ref: the updater
      // below runs lazily during render, after the ref may have moved on.
      const previous = lastExtractedRef.current;
      lastExtractedRef.current = extracted.text;
      setResume((prev) => {
        if (prev.trim().length === 0 || prev === previous) return extracted.text;
        return `${prev}\n\n${extracted.text}`;
      });
      setDocInfo({ name: file.name, chars: extracted.text.length });
      setUploadPhase('success');
      setUploadMessage(
        `Extracted ${extracted.text.length.toLocaleString('en-US')} characters from "${file.name}". Review the text before analyzing.`,
      );
    } catch {
      setUploadPhase('error');
      setUploadMessage(
        `Something went wrong while reading "${file.name}". Please try again.`,
      );
    }
  }, []);

  const handleRemoveDocument = useCallback(() => {
    setDocInfo(null);
    lastExtractedRef.current = null;
    setUploadPhase('idle');
    setUploadMessage('');
  }, []);

  const handleReviewImport = useCallback(() => {
    if (resume.trim().length === 0) return;
    const name = docInfo?.name ?? 'Pasted resume';
    setImportSession(createImportSession(name, resume));
    setActiveTab('profile');
  }, [docInfo, resume]);

  const handleBuildResume = useCallback(() => {
    setResumeDraft((current) => current ?? draftFromProfile(profile));
    setActiveTab('resume');
  }, [profile]);

  const handleRebuildResume = useCallback(() => {
    setResumeDraft(draftFromProfile(profile));
    setResumeVersions([]);
  }, [profile]);

  const handleSaveVersion = useCallback((name: string) => {
    setResumeDraft((current) => {
      if (!current) return current;
      setResumeVersions((versions) => [...versions, saveVersion(current, name)]);
      return current;
    });
  }, []);

  const handleRestoreVersion = useCallback((versionId: string) => {
    setResumeVersions((versions) => {
      const version = versions.find((candidate) => candidate.id === versionId);
      if (version) setResumeDraft(restoreVersion(version));
      return versions;
    });
  }, []);

  const handleAnalyze = useCallback(() => {
    const nextErrors = validateInputs(resume, job);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      setResult(null);
      setStatus('error');
      setMessage('Please fix the highlighted fields before analyzing.');
      return;
    }

    setStatus('loading');
    setMessage('');
    setResult(null);

    // Deterministic local analysis. Deferred by a frame so the loading state is
    // painted; no network, no model, nothing leaves the browser.
    window.setTimeout(() => {
      try {
        const analysis = analyzeMatch(resume, job);
        if (analysis.jobTermCount === 0) {
          setStatus('error');
          setMessage(
            'No matchable terms were found in the job description. Paste a more detailed posting.',
          );
          setResult(null);
          return;
        }
        setResult(analysis);
        setStatus('success');
        setMessage('Analysis complete. Review the results below.');
        requestAnimationFrame(() =>
          resultsRef.current?.focus({ preventScroll: true }),
        );
      } catch {
        setStatus('error');
        setResult(null);
        setMessage('Something went wrong while analyzing. Please try again.');
      }
    }, 0);
  }, [resume, job]);

  const handleClear = useCallback(() => {
    setResume('');
    setJob('');
    setErrors({});
    setResult(null);
    setStatus('idle');
    setMessage('');
    setUploadPhase('idle');
    setUploadMessage('');
    setDocInfo(null);
    lastExtractedRef.current = null;
  }, []);

  const loadSample = useCallback(() => {
    setResume(RESUME_SAMPLE);
    setJob(JOB_SAMPLE);
    setErrors({});
    setResult(null);
    setStatus('idle');
    setMessage('');
    setUploadPhase('idle');
    setUploadMessage('');
    setDocInfo(null);
    lastExtractedRef.current = null;
  }, []);

  const busy = status === 'loading';

  return (
    <AppShell activeTab={activeTab} onTabChange={setActiveTab}>
      {activeTab === 'analyze' && (
        <section
          id="analyze-panel"
          role="tabpanel"
          aria-labelledby="analyze-tab"
          className="flex flex-col gap-6"
        >
          <PageHeader
            eyebrow="Analyze"
            title="Compare a resume to a job description"
            description="Upload a CV or paste both texts to see keyword coverage, gaps, and concrete suggestions. Everything runs locally in your browser."
            action={
              <Button
                variant="secondary"
                icon={<RefreshIcon />}
                onClick={handleReviewImport}
                disabled={resume.trim().length === 0}
              >
                Review &amp; import into profile
              </Button>
            }
          />

          <div className="grid gap-6 lg:grid-cols-2">
            <form
              className="flex flex-col gap-5 rounded-lg border border-line bg-surface p-5 shadow-card sm:p-6"
              onSubmit={(e) => {
                e.preventDefault();
                handleAnalyze();
              }}
              aria-label="Resume and job description analyzer"
              noValidate
            >
              <ResumeUpload
                phase={uploadPhase}
                message={uploadMessage}
                docInfo={docInfo}
                onFileSelected={handleFileSelected}
                onRemoveDocument={handleRemoveDocument}
              />

              <TextAreaField
                label="Resume text"
                value={resume}
                onChange={setResume}
                hint="Your experience, skills, and education as plain text. Uploaded documents are placed here for review."
                placeholder="Paste your resume…"
                error={errors.resume}
              />

              <TextAreaField
                label="Job description"
                value={job}
                onChange={setJob}
                hint="The role requirements you want to match against."
                placeholder="Paste the job description…"
                error={errors.job}
              />

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" size="lg" disabled={busy} icon={<AnalyzeIcon />}>
                  {busy ? 'Analyzing…' : 'Analyze match'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleClear}>
                  Clear
                </Button>
                <Button type="button" variant="ghost" onClick={loadSample}>
                  Load sample
                </Button>
              </div>
            </form>

            <div
              ref={resultsRef}
              tabIndex={-1}
              className="rounded-lg outline-none"
              aria-live="polite"
              aria-busy={busy}
            >
              {status === 'idle' && <EmptyState />}
              {status === 'loading' && <LoadingState />}
              {status === 'error' && <ErrorState message={message} />}
              {status === 'success' && result && <ResultsPanel result={result} />}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'profile' && (
        <section
          id="profile-panel"
          role="tabpanel"
          aria-labelledby="profile-tab"
          className="flex flex-col gap-6"
        >
          <PageHeader
            eyebrow="Profile"
            title="Master professional profile"
            description="Your single record of facts. Confirm each one before it can appear on a resume — nothing is ever promoted automatically."
            action={
              <span className="inline-flex items-center gap-2 rounded-pill border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-soft">
                <ProfileIcon className="h-3.5 w-3.5" />
                Browser memory only
              </span>
            }
          />
          <ProfilePanel profile={profile} onChange={setProfile} />
          {importSession && (
            <ImportReviewPanel
              session={importSession}
              profile={profile}
              onChange={setImportSession}
              onProfileChange={setProfile}
              onClose={() => setImportSession(null)}
            />
          )}
          {!importSession && (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-line-strong bg-surface/70 p-6 text-sm text-ink-soft sm:flex-row sm:items-center sm:justify-between">
              <p>
                Open the Analyze tab, paste or upload a CV, then use “Review &amp;
                import into profile” to populate the profile.
              </p>
              <Button variant="secondary" onClick={() => setActiveTab('analyze')}>
                Go to Analyze
              </Button>
            </div>
          )}
        </section>
      )}

      {activeTab === 'resume' && (
        <section
          id="resume-panel"
          role="tabpanel"
          aria-labelledby="resume-tab"
          className="flex flex-col gap-6"
        >
          <PageHeader
            eyebrow="Resume"
            title="Resume builder"
            description="Turn confirmed profile facts into a tailored, ATS-friendly document. Editing the draft never changes your master profile."
          />
          {resumeDraft ? (
            <ResumeBuilder
              draft={resumeDraft}
              onChange={setResumeDraft}
              versions={resumeVersions}
              onSaveVersion={handleSaveVersion}
              onRestoreVersion={handleRestoreVersion}
              onRebuildFromProfile={handleRebuildResume}
            />
          ) : (
            <div className="flex flex-col items-start gap-4 rounded-lg border border-dashed border-line-strong bg-surface/70 p-6">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-accent-soft text-accent-strong"
                >
                  <ResumeIcon className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-ink">
                    Build a resume draft from your profile
                  </h2>
                  <p className="mt-1 max-w-prose text-sm text-ink-soft">
                    The draft is a copy of your confirmed facts, so editing it never
                    changes the profile. Save named versions and compare them at any
                    time.
                  </p>
                </div>
              </div>
              <Button size="lg" icon={<ResumeIcon />} onClick={handleBuildResume}>
                Build resume from profile
              </Button>
            </div>
          )}
        </section>
      )}
    </AppShell>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-72 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface/70 p-8 text-center">
      <span
        aria-hidden="true"
        className="flex h-14 w-14 items-center justify-center rounded-pill bg-accent-soft text-accent-strong"
      >
        <AnalyzeIcon className="h-7 w-7" />
      </span>
      <h2 className="text-lg font-semibold tracking-tight text-ink">No analysis yet</h2>
      <p className="max-w-prose text-sm text-ink-soft">
        Enter a resume and job description, then select{' '}
        <span className="font-semibold text-ink">Analyze match</span> to see
        coverage, matched terms, gaps, and suggestions.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div
      className="flex h-full min-h-72 flex-col items-center justify-center gap-3 rounded-lg border border-line bg-surface/80 p-8 text-center"
      role="status"
    >
      <span
        className="h-7 w-7 animate-spin rounded-full border-[3px] border-line-strong border-t-accent"
        aria-hidden="true"
      />
      <p className="text-sm font-medium text-ink-soft">
        Analyzing your texts locally…
      </p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="flex h-full min-h-72 flex-col items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger-soft p-8 text-center"
      role="alert"
    >
      <h2 className="text-lg font-semibold text-danger">Could not analyze</h2>
      <p className="max-w-prose text-sm text-ink">{message}</p>
    </div>
  );
}
